import { createContext, use } from 'react';

/** The signed-in person, as the shell needs to render them. */
export interface SessionUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
}

export interface Session {
  user: SessionUser | null;
  signOut: () => void;
  /**
   * Sign in again, as someone else, and come back here (spec 196 US2): the sign-in
   * form is shown even while the identity provider still remembers this person.
   * Optional so a test's hand-built session need not carry it; without it, a
   * caller falls back to `signOut`.
   */
  switchAccount?: () => void;
}

/**
 * Defaults to "nobody is signed in" rather than throwing when no provider is
 * mounted.
 *
 * That default is what lets the ~100 existing component tests keep rendering
 * untouched: almost none of them are about identity, and requiring a provider
 * would have made every one of them a test-harness change (spec 067 SC-015).
 * The handful of components that *do* show a person handle `null` as a real
 * state — which they must anyway, for the moment before the session resolves.
 */
const SessionContext = createContext<Session>({
  user: null,
  signOut: () => {},
});

export const SessionContextProvider = SessionContext.Provider;

export function useSession(): Session {
  return use(SessionContext);
}
